import difference from 'lodash/difference';
import union from 'lodash/union';

import { useCallback, useMemo, useState } from 'react';

type SelectionRecord = Record<string, unknown>;

export type UseSelectionTableWithInversionOptions<T, R> = {
	totalCount?: number;
	results?: T[];
	getRowKey: (record: T) => R;
};

export interface UseSelectionTableWithInversionResult<T, R> {
	handleTablePageSelectAll: (selected: boolean, records: T[]) => void;
	handleTableRowSelect: (record: T, selected: boolean) => void;
	pageSelectedKeys: R[];
	resetSelection: () => void;
	selectAll: () => void;
	selectAllEnabled: boolean;
	selectedCount: number;
	selectedKeys: R[];
	totalCount?: number;
	updateSelection: (rowKeys: R[], isSelected?: boolean) => void;
}

export function useSelectionTableWithInversion<T extends SelectionRecord, R extends number | string>(
	options: UseSelectionTableWithInversionOptions<T, R>
): UseSelectionTableWithInversionResult<T, R> {
	const { totalCount, results, getRowKey } = options;
	const [ selectAllEnabled, setSelectAllEnabled ] = useState(false);
	const [ selectedKeys, setSelectedKeys ] = useState<R[]>([]);

	const resetSelection = useCallback(() => {
		setSelectAllEnabled(false);
		setSelectedKeys([]);
	}, []);

	const pageSelectedKeys = useMemo(() => {
		const currentPageRowKeys = results?.map(getRowKey) ?? [];
		const negativeSelection = difference(currentPageRowKeys, selectedKeys);

		return selectAllEnabled ? negativeSelection : selectedKeys;
	}, [ results, getRowKey, selectAllEnabled, selectedKeys ]);

	const selectAll = useCallback(() => {
		setSelectedKeys([]);
		setSelectAllEnabled(true);
	}, [ setSelectAllEnabled, setSelectedKeys ]);

	const selectedCount = useMemo(
		() => (selectAllEnabled ? (totalCount ?? 0) - selectedKeys.length : selectedKeys.length),
		[ selectAllEnabled, totalCount, selectedKeys.length ]
	);

	const updateSelection = useCallback(
		(rowKeys: R[], isSelected?: boolean) => {
			const selected = isSelected ?? true;
			const doUnion = selected !== selectAllEnabled;
			const newSelectedRowKeys = doUnion ? union(selectedKeys, rowKeys) : difference(selectedKeys, rowKeys);

			if (selectAllEnabled && newSelectedRowKeys.length === totalCount) {
				resetSelection();
			} else {
				setSelectedKeys(newSelectedRowKeys);
			}

		},
		[ resetSelection, selectAllEnabled, selectedKeys, totalCount ]
	);

	const handleTableRowSelect = useCallback(
		(record: T, isSelected: boolean) => {
			updateSelection([ getRowKey(record) ], isSelected);
		},
		[ updateSelection, getRowKey ]
	);

	const handleTablePageSelectAll = useCallback(
		(selected: boolean, records: T[]) => {
			const pageRecords = results ?? [];
			const recordIds = records.filter(Boolean).map(getRowKey);
			const selection =
				selectAllEnabled && recordIds.length ? union(selectedKeys, recordIds) : pageRecords.map(getRowKey);

			updateSelection(selection, selected);
		},
		[ results, getRowKey, selectAllEnabled, selectedKeys, updateSelection ]
	);

	return {
		handleTablePageSelectAll,
		handleTableRowSelect,
		pageSelectedKeys,
		resetSelection,
		selectAll,
		selectAllEnabled,
		selectedCount,
		selectedKeys,
		totalCount,
		updateSelection
	};
}
